import { useRef, useEffect } from 'react';

/**
 * Utility functions for handling binary data and blobs
 */

/**
 * Converts a base64 string to a Blob object
 * @param base64Data - The base64 encoded string (with or without data URL prefix)
 * @param contentType - The MIME type of the data
 * @returns A Blob object containing the binary data
 */
export function base64ToBlob(base64Data: string, contentType: string = 'application/octet-stream'): Blob {
  // Remove data URL prefix if present
  const base64String = base64Data.includes(',') ? base64Data.split(',')[1] : base64Data;
  
  // Decode base64 string
  const binaryString = atob(base64String);
  
  // Create Uint8Array from binary string
  const bytes = new Uint8Array(binaryString.length);
  for (let i = 0; i < binaryString.length; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }
  
  // Create and return Blob
  return new Blob([bytes], { type: contentType });
}

/**
 * Converts a base64 string to a blob URL
 * @param base64Data - The base64 encoded string
 * @param contentType - The MIME type of the data
 * @returns A blob URL that can be used in img src, etc.
 */
export function base64ToBlobUrl(base64Data: string, contentType: string = 'application/octet-stream'): string {
  const blob = base64ToBlob(base64Data, contentType);
  return URL.createObjectURL(blob);
}

/**
 * Hook for managing blob URLs with automatic cleanup
 * @returns Functions for creating and managing blob URLs
 */
export function useBlobUrls() {
  const blobUrlsRef = useRef<string[]>([]);
  
  const createBlobUrl = (base64Data: string, contentType: string = 'application/octet-stream'): string => {
    const url = base64ToBlobUrl(base64Data, contentType);
    blobUrlsRef.current.push(url);
    return url;
  };
  
  useEffect(() => {
    const urls = blobUrlsRef.current;
    return () => {
      urls.forEach(url => URL.revokeObjectURL(url));
    };
  }, []);
  
  return {
    createBlobUrl,
  };
}
